import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import { PAYMENT_MODEL_ENUM } from '@nxsys/constants/AppEnums';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { showMessage } from '@nxsys/utils/message';
import { Col, Form, Row } from 'antd';
import { FormProps } from 'antd/lib';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { isEqual } from 'lodash';
import AppCurrencyInput from '@nxsys/components/AppCurrencyInput';

type BalanceModalProps = {
  isOpen: boolean;
  toggle: () => void;
  onSubmit: (params: BalanceValues) => Promise<void>;
  defaultValues?: Partial<BalanceValues>;
  // Match umbrella-fe's !isPaye condition. Employer costs only show for
  // umbrella/hybrid workers; PAYEE/PAYE_HYBRID/PAYE_FTH/CIS hide them.
  paymentModel?: string;
};

export type BalanceValues = {
  balance: number;
  ERNI?: number;
  appLevy?: number;
  ERPension?: number;
};

const BalanceModal: React.FC<BalanceModalProps> = ({
  isOpen,
  toggle,
  onSubmit,
  defaultValues = {},
  paymentModel,
}: BalanceModalProps) => {
  const { messages } = useTypedIntl();

  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const baselineRef = useRef<{
    balance: number | undefined;
    appLevy: number | undefined;
    ERNI: number | undefined;
    ERPension: number | undefined;
  }>({
    balance: undefined,
    appLevy: undefined,
    ERNI: undefined,
    ERPension: undefined,
  });

  // Show ERNI / App Levy / Employer's Pension only for umbrella-style workers,
  // mirroring umbrella-fe ModalUpdateHoliday.js `!isPaye` condition.
  const showEmployerCosts = useMemo(
    () =>
      paymentModel === PAYMENT_MODEL_ENUM.UMBRELLA ||
      paymentModel === PAYMENT_MODEL_ENUM.HYBRID,
    [paymentModel],
  );

  const normalize = (v: unknown) =>
    v == null || v === '' ? undefined : Number(v);

  const normalizeAll = (obj: Record<string, unknown>) => ({
    balance: normalize(obj.balance),
    appLevy: normalize(obj.appLevy),
    ERNI: normalize(obj.ERNI),
    ERPension: normalize(obj.ERPension),
  });

  useEffect(() => {
    if (isOpen) {
      form.setFieldsValue(defaultValues);
      baselineRef.current = normalizeAll(
        defaultValues as Record<string, unknown>,
      );
      setIsDirty(false);
    }
  }, [isOpen]);

  const onFinish: FormProps['onFinish'] = async (values) => {
    if (isEqual(normalizeAll(values), baselineRef.current)) {
      showMessage({
        type: 'warning',
        content: messages['common.noChangesToSave'] || 'No changes to save',
      });
      return;
    }
    setIsSubmitting(true);
    // Strip employer cost fields for PAYEE-style workers so backend ignores
    // them, same behaviour as umbrella-fe (isPaye ? undefined : value).
    const payload: BalanceValues = showEmployerCosts
      ? values
      : {
          balance: values.balance,
        };
    await onSubmit(payload);
    setIsSubmitting(false);
  };

  const onFinishFailed: FormProps['onFinishFailed'] = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  return (
    <AppModalContainer
      title={messages['common.submitHolidayPay']}
      isOpen={isOpen}
      toggle={toggle}
      actions={[
        {
          name: messages['common.cancel'],
          variant: 'outlined',
          onClick: toggle,
        },
        {
          name: messages['common.save'],
          buttonType: 'primary',
          onClick: () => form.submit(),
          loading: isSubmitting,
          disabled: isSubmitting || !isDirty,
        },
      ]}
      minHeight="200px"
    >
      <AppFormContainer
        form={form}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        onValuesChange={(_changed: any, allValues: Record<string, unknown>) => {
          setIsDirty(!isEqual(normalizeAll(allValues), baselineRef.current));
        }}
      >
        <Row gutter={16}>
          <Col xs={24} sm={showEmployerCosts ? 12 : 24}>
            <Form.Item
              label={messages['common.balance']}
              name="balance"
              rules={[{ required: true, message: messages['form.pleaseEnter'] }]}
            >
              <AppCurrencyInput />
            </Form.Item>
          </Col>
          {showEmployerCosts && (
            <>
              <Col xs={24} sm={12}>
                <Form.Item
                  label={messages['common.appLevy']}
                  name="appLevy"
                >
                  <AppCurrencyInput />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  label={messages['common.ERNI']}
                  name="ERNI"
                  rules={[
                    { required: true, message: messages['form.pleaseEnter'] },
                  ]}
                >
                  <AppCurrencyInput />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  label={messages['common.employerPension']}
                  name="ERPension"
                >
                  <AppCurrencyInput />
                </Form.Item>
              </Col>
            </>
          )}
        </Row>
      </AppFormContainer>
    </AppModalContainer>
  );
};

export default BalanceModal;
